import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  Activity, Bell, LineChart, Map, Cpu, Tag as TagIcon,
  Users as UsersIcon, Building2, LogOut, FileBarChart2, Radio, Antenna, Menu
} from "lucide-react";

// Super Admin: everything
const superAdminNav = [
  { to: "/dashboard", label: "Overview", icon: Activity, testId: "nav-dashboard" },
  { to: "/mimics", label: "Mimics", icon: Map, testId: "nav-mimics" },
  { to: "/alarms", label: "Alarms", icon: Bell, testId: "nav-alarms" },
  { to: "/trends", label: "Trends", icon: LineChart, testId: "nav-trends" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testId: "nav-reports" },
  { to: "/sites", label: "Sites", icon: Building2, testId: "nav-sites" },
  { to: "/devices", label: "Devices", icon: Cpu, testId: "nav-devices" },
  { to: "/tags", label: "Tags", icon: TagIcon, testId: "nav-tags" },
  { to: "/ingestion", label: "Ingestion", icon: Antenna, testId: "nav-ingestion" },
  { to: "/users", label: "Users", icon: UsersIcon, testId: "nav-users" },
];

// Plant Admin: config within their sites, no user management
const plantAdminNav = [
  { to: "/dashboard", label: "Overview", icon: Activity, testId: "nav-dashboard" },
  { to: "/mimics", label: "Mimics", icon: Map, testId: "nav-mimics" },
  { to: "/alarms", label: "Alarms", icon: Bell, testId: "nav-alarms" },
  { to: "/trends", label: "Trends", icon: LineChart, testId: "nav-trends" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testId: "nav-reports" },
  { to: "/devices", label: "Devices", icon: Cpu, testId: "nav-devices" },
  { to: "/tags", label: "Tags", icon: TagIcon, testId: "nav-tags" },
  { to: "/ingestion", label: "Ingestion", icon: Antenna, testId: "nav-ingestion" },
];

const clientNav = [
  { to: "/dashboard", label: "Overview", icon: Activity, testId: "nav-dashboard" },
  { to: "/mimics", label: "Mimics", icon: Map, testId: "nav-mimics" },
  { to: "/alarms", label: "Alarms", icon: Bell, testId: "nav-alarms" },
  { to: "/trends", label: "Trends", icon: LineChart, testId: "nav-trends" },
  { to: "/reports", label: "Reports", icon: FileBarChart2, testId: "nav-reports" },
];

const ROLE_LABEL = { super_admin: "SUPER ADMIN", plant_admin: "PLANT ADMIN", client: "CLIENT", admin: "SUPER ADMIN" };

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = user?.role === "super_admin" || user?.role === "admin"
    ? superAdminNav
    : user?.role === "plant_admin" ? plantAdminNav : clientNav;

  const [isCollapsed, setIsCollapsed] = React.useState(() => {
    return localStorage.getItem("scada_sidebar_collapsed") === "true";
  });
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);

  const toggleCollapse = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem("scada_sidebar_collapsed", next ? "true" : "false");
      return next;
    });
  };

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-[#0B0D0F] text-[#E3E8EE] overflow-x-hidden">
      {/* Mobile Backdrop Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar Aside */}
      <aside
        className={`border-r border-[#2C3238] bg-[#14171A]/80 backdrop-blur-md flex flex-col h-screen shrink-0 transition-all duration-300 ease-in-out z-50
          fixed inset-y-0 left-0
          ${isCollapsed ? "md:w-16" : "md:w-64"}
          ${isMobileOpen ? "translate-x-0 w-64" : "-translate-x-full md:translate-x-0"}
        `}
        data-testid="sidebar"
      >
        <div className={`h-14 flex items-center border-b border-[#2C3238] gap-3 ${isCollapsed ? "px-1 justify-center" : "px-4"}`}>
          <img className="text-[#00E5FF]" height="28" width="56" alt="ST-Scada Logo" src="/images/st-logo.png" />
          <div className={`transition-opacity duration-300 ${isCollapsed ? "md:hidden" : ""}`}>
            <div className="font-heading font-black text-base leading-none">ST<span className="text-[#00E5FF]">.SCADA</span></div>
            <div className="scada-label leading-none mt-1 text-[8px] text-[#5A6573]">v1.0 // industrial</div>
          </div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              onClick={() => setIsMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center border-l-2 transition-all duration-200 ${isCollapsed ? "px-0 justify-center h-12" : "px-5 py-3 gap-3"
                } ${isActive
                  ? "text-[#00E5FF] bg-gradient-to-r from-[#00E5FF]/10 to-transparent border-[#00E5FF] text-glow-accent"
                  : "text-[#9BA4B5] hover:text-[#E3E8EE] hover:bg-[#1E2226]/40 border-transparent hover:border-[#00E5FF]/30"
                }`
              }
              end={it.to === "/dashboard"}
            >
              <it.icon size={15} />
              <span className={`font-mono-metric uppercase tracking-wider text-[0.7rem] transition-all duration-200 ${isCollapsed ? "md:hidden" : ""}`}>{it.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-[#2C3238] p-4 space-y-3">
          {isCollapsed ? (
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 rounded-none bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center text-[#00E5FF] font-heading font-black text-[10px]" title={user?.name}>
                {user?.name?.charAt(0) || "U"}
              </div>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                title="Sign Out"
                className="w-8 h-8 flex items-center justify-center border border-[#2C3238] hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] text-[#9BA4B5] transition-all duration-200"
              >
                <LogOut size={12} />
              </button>
            </div>
          ) : (
            <>
              <div className="bg-[#0B0D0F]/70 border border-[#2C3238]/60 p-3 rounded-none relative overflow-hidden">
                <div className="absolute top-0 right-0 w-1.5 h-1.5 bg-[#00E676] rounded-none animate-pulse" />
                <div className="scada-label text-[8px] text-[#5A6573] mb-1">Operator Profile</div>
                <div className="text-[#E3E8EE] font-bold truncate text-xs" data-testid="user-name">{user?.name}</div>
                <div className="text-[#9BA4B5] text-[9px] truncate mt-0.5">{user?.email}</div>
                <div className="text-[#00E5FF] text-[8px] font-mono-metric uppercase tracking-widest mt-2 px-1.5 py-0.5 bg-[#00E5FF]/10 border border-[#00E5FF]/20 inline-block">{ROLE_LABEL[user?.role] || user?.role}</div>
              </div>
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 border border-[#2C3238] hover:border-[#FF3B30] hover:bg-[#FF3B30]/10 hover:text-[#FF3B30] text-[#9BA4B5] py-2 font-mono-metric uppercase tracking-wider text-[10px] transition-all duration-200"
              >
                <LogOut size={12} /> Sign Out
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={`flex-1 flex flex-col min-w-0 h-screen overflow-auto transition-all duration-300 ease-in-out ${isCollapsed ? "md:pl-16" : "md:pl-64"}`}>
        <header className="sticky top-0 z-40 h-14 border-b border-[#2C3238]/60 flex items-center justify-between px-4 md:px-6 bg-[#14171A]" data-testid="topbar">
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                if (window.innerWidth < 768) {
                  setIsMobileOpen(!isMobileOpen);
                } else {
                  toggleCollapse();
                }
              }}
              className="text-[#9BA4B5] hover:text-[#E3E8EE] p-1.5 border border-[#2C3238] bg-[#0B0D0F]/50 mr-1 hover:border-[#00E5FF] transition-colors"
              title="Toggle Sidebar"
              data-testid="sidebar-toggle"
            >
              <Menu size={16} />
            </button>
            <div className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E676] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00E676]"></span>
            </div>
            <span className="scada-label text-[#00E676] text-[9px] tracking-widest text-glow-normal">SYSTEM ACTIVE</span>
            <span className="text-[#2C3238]/40 hidden xs:inline">|</span>
            <span className="font-mono-metric text-[10px] text-[#9BA4B5] hidden sm:inline">{new Date().toLocaleString()}</span>
          </div>
          <div className="font-mono-metric text-[8px] uppercase tracking-[0.3em] text-[#00E5FF] px-2 py-0.5 border border-[#00E5FF]/20 bg-[#00E5FF]/5 text-glow-accent truncate max-w-[150px] xs:max-w-none">
            CONTROL CONSOLE // LIVE
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6">{children}</main>
      </div>
    </div>
  );
}
